import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
  Stack,
  useTheme
} from '@mui/material';
import Loader from 'src/common/Loader';
import { useDispatch } from 'react-redux';
import { addNewSite, updateSiteDetails } from 'src/redux/siteSlice';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import CountryStateFields from 'src/common/formFields/CountryStateFields';
import InputField from 'src/common/formFields/InputField';
import SelectField from 'src/common/formFields/SelectField';
import { TIMEZONE_OPTIONS } from 'src/utils/timezoneUtils';
import {
  siteFormInitialValues,
  siteFormModel,
  siteValidationSchema
} from './siteFormModel';

const NewSite = ({
  open, handleClose, initialValues, embedded = false, onSaved
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const formInitValues = initialValues || siteFormInitialValues;
  const isEditMode = Boolean(initialValues);
  const {
    formId,
    formField: {
      siteName,
      siteCode,
      addressLine,
      city,
      state,
      country,
      timezone,
      pinCode,
      latitude,
      longitude
    }
  } = siteFormModel;

  const onSubmit = async (values, actions) => {
    setLoading(true);
    try {
      if (initialValues) {
        const result = await dispatch(updateSiteDetails(values)).unwrap();
        onSaved?.(result);
      } else {
        const result = await dispatch(addNewSite(values)).unwrap();
        onSaved?.(result);
      }
      handleClose();
      setLoading(false);
      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Site updated successfully'
            : 'New site created successfully'
        })
      );
    } catch (error) {
      console.error('error', error);
      actions.setSubmitting(false);
      setLoading(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message || 'An error occurred' }));
    }
  };

  const requiredHintText = '';

  const formContent = (
    <Formik
      initialValues={formInitValues}
      validationSchema={siteValidationSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <>
          {loading ? (
            <Loader />
          ) : (
            <Form
              id={formId}
              className="csp-form csp-form--80vh"
            >
              <DialogContent
                sx={{
                  p: 3,
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto'
                }}
              >
                {!isEditMode && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 2 }}
                >
                  * Required fields
                </Typography>
                )}
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <InputField
                      name={siteName.name}
                      label={`${siteName.label} *`}
                      required
                      requiredHintText={requiredHintText}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputField
                      name={siteCode.name}
                      label={`${siteCode.label} *`}
                      required
                      requiredHintText={requiredHintText}
                      upperCase
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputField
                      name={city.name}
                      label={`${city.label} *`}
                      required
                      requiredHintText={requiredHintText}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputField
                      name={pinCode.name}
                      label={`${pinCode.label} *`}
                      required
                      requiredHintText={requiredHintText}
                      fullWidth
                    />
                  </Grid>
                  <CountryStateFields
                    countryField={{ ...country, label: `${country.label} *` }}
                    stateField={{ ...state, label: `${state.label} *` }}
                    requiredHintText={requiredHintText}
                  />
                  <Grid item xs={12} sm={6}>
                    <SelectField
                      name={timezone.name}
                      label={timezone.label}
                      data={TIMEZONE_OPTIONS}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputField
                      name={latitude.name}
                      label={latitude.label}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputField
                      name={longitude.name}
                      label={longitude.label}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputField
                      name={addressLine.name}
                      label={`${addressLine.label} *`}
                      required
                      requiredHintText={requiredHintText}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions
                sx={{
                  px: 3,
                  py: 1.75,
                  mt: 1,
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 2,
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1.25,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  boxShadow: '0 -8px 16px rgba(15, 23, 42, 0.06)',
                  bgcolor: 'rgba(255, 255, 255, 0.96)',
                  backdropFilter: 'blur(8px)'
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.25}
                >
                  <Button
                    onClick={handleClose}
                    variant="contained"
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      minWidth: 110
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      minWidth: 130
                    }}
                  >
                    {initialValues ? 'Update Site' : 'Create Site'}
                  </Button>
                </Stack>
              </DialogActions>
            </Form>
          )}
        </>
      )}
    </Formik>
  );

  if (embedded) return formContent;

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <FMSModalTitle
        title={initialValues ? 'EDIT SITE' : 'ADD NEW SITE'}
        onCancel={handleClose}
      />
      {formContent}
    </FMSModal>
  );
};

NewSite.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  embedded: PropTypes.bool,
  onSaved: PropTypes.func,
};

export default NewSite;
